<script setup lang="ts">
import type { OTranscriptProps } from '@ai/ui-components'
import type { ActiveCall } from 'shared/src'
import LiveCallDetailsPanel from '@/components/BusinessLogic/CallDetails/LiveCallDetailsPanel.vue'

const calls = ref<ActiveCall[]>([
  {
    id: 'debug-call-fr',
    callerNumber: '+33 1 84 80 20 20',
    roomName: 'debug-room-fr',
    startTime: Date.now() - 74_000,
    durationSec: 74,
    language: 'fr',
    status: ['OK'],
  },
  {
    id: 'debug-call-en',
    callerNumber: '+44 20 7946 0958',
    roomName: 'debug-room-en',
    startTime: Date.now() - 112_000,
    durationSec: 112,
    language: 'en',
    status: ['confusion', 'complaint'],
  },
])

const transcript: OTranscriptProps['transcript'] = [
  {
    id: 'debug-agent-1',
    speaker: 'agent',
    text: 'Bonjour, comment puis-je vous aider aujourd’hui ?',
    startSec: 0,
    endSec: 5,
  },
  {
    id: 'debug-customer-1',
    speaker: 'customer',
    text: 'I need help understanding the latest invoice on my account.',
    startSec: 6,
    endSec: 14,
    badges: ['confusion'],
  },
  {
    id: 'debug-agent-2',
    speaker: 'agent',
    text: 'I found the invoice. Let me explain each charge and the payment status.',
    startSec: 15,
    endSec: 25,
  },
  {
    id: 'debug-customer-2',
    speaker: 'customer',
    text: 'Please send me a copy after the call.',
    startSec: 27,
    endSec: 33,
    badges: ['complaint'],
  },
]

const events: NonNullable<OTranscriptProps['events']> = [
  { id: 'debug-va-start', type: 'va', timeSec: 0, label: 'call_started' },
  {
    id: 'debug-tool-invoice',
    type: 'tool',
    timeSec: 15,
    label: 'find_invoice_by_customer_id',
    detail: 'Tool used',
    segmentId: 'debug-agent-2',
  },
  {
    id: 'debug-variable-email',
    type: 'var',
    timeSec: 29,
    label: 'customer_email',
    detail: 'Variable populated',
    segmentId: 'debug-customer-2',
  },
]

const panelOpen = ref(true)
const currentCallIndex = ref(0)
const listening = ref(false)
const disconnected = ref(false)
const loading = ref(false)

const currentCall = computed(() => calls.value[currentCallIndex.value])
const hasPrevious = computed(() => currentCallIndex.value > 0)
const hasNext = computed(() => currentCallIndex.value < calls.value.length - 1)
const currentStatus = computed(() => currentCall.value.status.at(-1))
const statusColor = computed(() => (currentStatus.value === 'OK' ? 'success' : 'warning'))
const audio = computed(() => ({
  mode: 'live' as const,
  durationSec: currentCall.value.durationSec,
  isListening: listening.value,
  canListen: !disconnected.value,
}))

const navigate = (direction: 'left' | 'right') => {
  const delta = direction === 'left' ? -1 : 1
  currentCallIndex.value = Math.min(
    Math.max(currentCallIndex.value + delta, 0),
    calls.value.length - 1,
  )
  listening.value = false
  disconnected.value = false
}

const reset = () => {
  listening.value = false
  disconnected.value = false
  loading.value = false
  panelOpen.value = true
}

let durationTimer: ReturnType<typeof setInterval> | undefined

onMounted(() => {
  durationTimer = setInterval(() => {
    if (!disconnected.value && !loading.value) {
      currentCall.value.durationSec = (currentCall.value.durationSec ?? 0) + 1
    }
  }, 1000)
})

onUnmounted(() => {
  if (durationTimer) clearInterval(durationTimer)
})
</script>

<template>
  <main class="min-h-screen bg-neutral-6 p-xl text-neutral-1" data-testid="live-supervision-debug">
    <section class="flex max-w-[520px] flex-col gap-l">
      <div>
        <h1 class="typography-heading-1">Live Supervision debug</h1>
        <p class="mt-xs typography-body-2 text-neutral-2">
          Local fixtures only. No realtime or LiveKit connection is created.
        </p>
      </div>

      <div class="flex flex-wrap gap-m">
        <OButton size="M" variant="secondary" label="Open panel" @click="panelOpen = true" />
        <OButton size="M" variant="secondary" label="Reset state" @click="reset" />
      </div>

      <div class="flex flex-col gap-m radius-m border border-neutral-5 bg-neutral-0 p-l">
        <OSwitch v-model="loading" label="Loading context" dataTestid="debug-loading" />
        <OSwitch v-model="listening" label="Listening" dataTestid="debug-listening" />
        <OSwitch v-model="disconnected" label="Disconnected" dataTestid="debug-disconnected" />
      </div>
    </section>

    <LiveCallDetailsPanel
      v-model:open="panelOpen"
      :call="currentCall"
      :agent-name="currentCallIndex === 0 ? 'FreePro RAG + Classification' : 'Billing Assistant'"
      :status-color="statusColor"
      :status-label="currentStatus"
      :left-disabled="!hasPrevious"
      :right-disabled="!hasNext"
      :loading="loading"
      :disconnected="disconnected"
      :listening="listening"
      :audio="audio"
      :transcript="transcript"
      :events="events"
      @navigate="navigate"
      @toggle-listening="listening = !listening"
      @disconnect="disconnected = true"
    />
  </main>
</template>
